import { useMemo, useState } from "react";
import { ScrollText } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
const logSources = ["audit"];
import { PageHeader, DataTable, EmptyState, AsyncBoundary, RowsSkeleton } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1">
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 border-line bg-surface" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="border-line bg-popover">
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function Logs() {
  const { data, loading, error } = useAsync(() => api.getLogs(), [], 20000);
  const [server, setServer] = useState("all");
  const [source, setSource] = useState("all");
  const [range, setRange] = useState("24h");

  const logs = useMemo(() => (data || []).filter((l) =>
    (server === "all" || l.server === server) && (source === "all" || l.source === source) && (Date.now()-new Date(l.timestamp).getTime() < ({"1h":3600000,"24h":86400000,"7d":604800000,"30d":2592000000}[range]))), [data, server, source, range]);

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader
        icon={ScrollText}
        title="Audit Logs"
        description="Auditoria real das últimas 200 ações. Logs Docker/journal ainda não implementados."
        breadcrumb={<><span>Infraestrutura</span><span>/</span><span className="text-foreground">Logs</span></>}
        
      />

      <div className="grid grid-cols-2 gap-3 rounded-xl border border-line bg-surface p-4 sm:grid-cols-3 lg:grid-cols-6">
        <FilterSelect label="Servidor" value={server} onChange={setServer} testid="logs-server"
          options={[{ value: "all", label: "Todos" }, ...[...new Set((data||[]).map(l=>l.server).filter(s=>s!=="—"))].map((s) => ({ value: s, label: s }))]} />
        <FilterSelect label="Source" value={source} onChange={setSource} testid="logs-source"
          options={[{ value: "all", label: "Todos" }, ...logSources.map((s) => ({ value: s, label: s }))]} />
        <FilterSelect label="Intervalo" value={range} onChange={setRange} testid="logs-range"
          options={["1h", "24h", "7d", "30d"].map((r) => ({ value: r, label: r }))} />
      </div>

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton rows={8} />}>
        {logs.length ? <DataTable testid="audit-logs-table" rows={logs} keyField="id" columns={[
          { key: "ts", header: "Timestamp" }, { key: "actor", header: "Ator" },
          { key: "action", header: "Ação", render: (row) => <span className="font-mono text-xs">{row.action}</span> },
          { key: "resource", header: "Recurso", render: (row) => <span className="font-mono text-xs">{row.resourceType} · {row.resourceId}</span> },
          { key: "scope", header: "Contexto" }, { key: "result", header: "Resultado", render: (row) => <span className={row.result === "success" ? "text-ok" : "text-err"}>{row.result}</span> },
        ]} /> : <EmptyState icon={ScrollText} title="Nenhum evento de auditoria" description="Ações administrativas e operações reais aparecerão aqui." />}
      </AsyncBoundary>
    </div>
  );
}
